import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { ComponentType, Overlay } from '@angular/cdk/overlay';
import { inject, Injectable } from '@angular/core';
import { PlatformService } from '@ld.consumer-portal/utils';
import {
    UnsavedChangesDialogComponent,
    UnsavedChangesDialogData,
    UnsavedChangesDialogResult,
} from './unsaved-changes-dialog/unsaved-changes-dialog.component';

export interface ModalOpenConfig<D = unknown> {
  data?: D;
  disableClose?: boolean;
  width?: string;
  maxWidth?: string;
  panelClass?: string | string[];
  hasBackdrop?: boolean;
  autoFocus?: DialogConfig['autoFocus'];
  ariaLabel?: string;
  ariaLabelledBy?: string;
}

@Injectable({ providedIn: 'root' })
export class ModalService {
  private readonly dialog = inject(Dialog);
  private readonly overlay = inject(Overlay);
  private readonly platformService = inject(PlatformService);

  open<R = unknown, D = unknown, C = unknown>(
    component: ComponentType<C>,
    config?: ModalOpenConfig<D>,
  ): DialogRef<R, C> {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    return this.dialog.open<R, D, C>(component, {
      data: config?.data,
      hasBackdrop: config?.hasBackdrop ?? true,
      disableClose: config?.disableClose ?? false,
      autoFocus: config?.autoFocus ?? (this.platformService.isNativeMobile() ? 'dialog' : 'first-tabbable'),
      backdropClass: ['cdk-overlay-dark-backdrop', 'ld-modal-backdrop'],
      panelClass: ['ld-modal-panel', ...(this.normalizePanelClass(config?.panelClass) ?? [])],
      width: config?.width,
      maxWidth: config?.maxWidth,
      ariaLabel: config?.ariaLabel,
      ariaLabelledBy: config?.ariaLabelledBy,
      positionStrategy,
    });
  }

  openUnsavedChangesDialog(
    data?: UnsavedChangesDialogData,
  ): DialogRef<UnsavedChangesDialogResult, UnsavedChangesDialogComponent> {
    const isMobile = this.platformService.isNativeMobile();

    const positionStrategy = isMobile
      ? this.overlay.position().global().bottom('0').centerHorizontally()
      : this.overlay.position().global().centerHorizontally().centerVertically();

    return this.dialog.open<UnsavedChangesDialogResult, UnsavedChangesDialogData, UnsavedChangesDialogComponent>(
      UnsavedChangesDialogComponent,
      {
        data: data ?? {},
        hasBackdrop: true,
        disableClose: true,
        autoFocus: 'dialog',
        backdropClass: ['cdk-overlay-dark-backdrop', 'ld-modal-backdrop'],
        panelClass: isMobile ? ['ld-bottom-sheet-panel'] : ['ld-modal-panel'],
        positionStrategy,
      },
    );
  }

  private normalizePanelClass(panelClass?: string | string[]): string[] {
    if (!panelClass) return [];
    return Array.isArray(panelClass) ? panelClass : [panelClass];
  }
}
